import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import LoadingIndicator from 'app/components/LoadingIndicator';
import { useStyles } from 'app/pages/DealerSettingsPage/style';
import { MyautoiqSegmentSettingsComponent } from 'app/pages/MyautoiqSegmentSettingsComponent';
import { MyautoiqTemplateGenerationSettingsComponent } from 'app/pages/MyautoiqTemplateGenerationSettingsComponent';
import { MyautoiqNurturingJourneySettingsComponent } from 'app/pages/MyautoiqNurturingJourneySettingsComponent';
import { selectMyautoiqSegmentSettings } from 'app/pages/MyautoiqSegmentSettingsComponent/selectors';
import { selectMyautoiqTemplateGenerationSettings } from 'app/pages/MyautoiqTemplateGenerationSettingsComponent/selectors';
import { selectMyautoiqNurturingJourneySettings } from 'app/pages/MyautoiqNurturingJourneySettingsComponent/selectors';
import { fetchMakes } from 'app/pages/MyautoiqSettingsPage/apiService/myautoiqSettingsApiService';
import { HeaderMakeSelect } from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderMakeSelect';
import { MakeItem } from 'app/components/ReusableUIComponents/LowLevelComponents/makeSearchUtils';
import { MakePageHeaderEntityInfo } from 'app/components/ReusableUIComponents/LowLevelComponents/PageHeaderEntityInfo';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

type SelectedMake = MakeItem;

const SELECTED_MAKE_STORAGE_KEY = 'selectedMake';

export default function MyautoiqSettingsPage() {
  const classes = useStyles();
  const location = useLocation();

  const myautoiqSegmentSettingsDetails = useSelector(
    selectMyautoiqSegmentSettings,
  );
  const myautoiqTemplateGenerationSettingsDetails = useSelector(
    selectMyautoiqTemplateGenerationSettings,
  );
  const myautoiqNurturingJourneySettingsDetails = useSelector(
    selectMyautoiqNurturingJourneySettings,
  );

  const [makes, setMakes] = React.useState<any[]>([]);
  const [selectedMake, setSelectedMake] = React.useState<SelectedMake | null>(
    null,
  );
  const [loadingMakes, setLoadingMakes] = React.useState(false);

  // Load make from localStorage on mount, unless a make_id is supplied in the URL.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const makeIdFromQuery = params.get('make_id');
    if (makeIdFromQuery) {
      return;
    }

    const savedMake = localStorage.getItem(SELECTED_MAKE_STORAGE_KEY);
    if (savedMake) {
      try {
        setSelectedMake(JSON.parse(savedMake));
      } catch (error) {
        console.error('Error parsing saved make:', error);
        localStorage.removeItem(SELECTED_MAKE_STORAGE_KEY);
      }
    }
  }, [location.search]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const makeIdFromQuery = params.get('make_id');
    if (!makeIdFromQuery || makes.length === 0) {
      return;
    }

    const make = makes.find((item: any) => item.make_id === makeIdFromQuery);
    if (!make) {
      return;
    }

    const makeData: SelectedMake = {
      make_id: make.make_id,
      make_name: make.make_name,
      manufacturer_id: make.manufacturer_id,
      manufacturer_name: make.manufacturer_name,
    };
    setSelectedMake(makeData);
    localStorage.setItem(SELECTED_MAKE_STORAGE_KEY, JSON.stringify(makeData));
  }, [location.search, makes]);

  // Fetch the lightweight makes list used by the brand-level settings selector.
  React.useEffect(() => {
    const loadMakes = async () => {
      setLoadingMakes(true);
      try {
        const response = await fetchMakes();
        const makesList = response?.body || [];
        setMakes(Array.isArray(makesList) ? makesList : []);
      } catch (error) {
        console.error('Error loading makes:', error);
        setMakes([]);
      } finally {
        setLoadingMakes(false);
      }
    };

    loadMakes();
  }, []);

  const makeOptions: MakeItem[] = React.useMemo(
    () =>
      makes.map((make: any) => ({
        make_id: make.make_id,
        make_name: make.make_name,
        manufacturer_id: make.manufacturer_id,
        manufacturer_name: make.manufacturer_name,
      })),
    [makes],
  );

  const handleMakeSelect = (make: MakeItem) => {
    setSelectedMake(make);
    localStorage.setItem(SELECTED_MAKE_STORAGE_KEY, JSON.stringify(make));
  };

  return (
    <>
      <Helmet>
        <title>Myautoiq Settings</title>
        <meta name="description" content="Myautoiq Settings - Myautoiq" />
      </Helmet>

      {(myautoiqSegmentSettingsDetails.loading ||
        myautoiqTemplateGenerationSettingsDetails.loading ||
        myautoiqNurturingJourneySettingsDetails.loading) && <LoadingIndicator />}

      <div className={classes.root} data-guidance-tour="myautoiq-settings-page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            Myautoiq Settings
            {selectedMake && (
              <Box sx={{ ml: 1.875 }}>
                <MakePageHeaderEntityInfo
                  makeName={selectedMake.make_name}
                  manufacturerName={selectedMake.manufacturer_name}
                  makeId={selectedMake.make_id}
                  manufacturerId={selectedMake.manufacturer_id}
                />
              </Box>
            )}
          </Typography>

          <div
            data-guidance-tour="myautoiq-manufacturer"
            style={{ marginRight: '30px' }}
          >
            <HeaderMakeSelect
              makesList={makeOptions}
              selectedMake={selectedMake}
              loading={loadingMakes}
              onSelect={handleMakeSelect}
            />
          </div>
        </div>

        {!selectedMake ? (
          <Typography
            variant="h6"
            gutterBottom
            component="div"
            className={classes.dealerTypography}
            sx={{ textAlign: 'center', marginTop: 4, color: 'text.secondary' }}
          >
            Please select a make to load settings
          </Typography>
        ) : (
          <>
            <MyautoiqSegmentSettingsComponent
              manufacturerId={selectedMake.manufacturer_id}
              makeId={selectedMake.make_id}
              makeName={selectedMake.make_name}
              manufacturerName={selectedMake.manufacturer_name}
            />
            <MyautoiqTemplateGenerationSettingsComponent
              manufacturerId={selectedMake.manufacturer_id}
              makeId={selectedMake.make_id}
              makeName={selectedMake.make_name}
              manufacturerName={selectedMake.manufacturer_name}
            />
            <MyautoiqNurturingJourneySettingsComponent
              manufacturerId={selectedMake.manufacturer_id}
              makeId={selectedMake.make_id}
              makeName={selectedMake.make_name}
              manufacturerName={selectedMake.manufacturer_name}
            />
          </>
        )}
      </div>
    </>
  );
}
